import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import MenuContent from "./MenuContent";
import OptionsMenu from "./OptionsMenu";
import BusinessIcon from "@mui/icons-material/Business";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { DivisionList } from "../../Services/Services";
import { storeDivision } from "../../Services/LocalStorageService";

const drawerWidth = 205;
const collapsedWidth = 65;

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: theme.transitions.create(["width", "background-color"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),

  [`& .${drawerClasses.paper}`]: {
    width: open ? drawerWidth : collapsedWidth,
    overflowX: "hidden",
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(["width", "background-color"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

// Styled Division Card Component
const DivisionCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.primary.main}30`,
  padding: theme.spacing(1.5),
  margin: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
    borderColor: theme.palette.primary.main,
  },
}));

export default function SideMenu() {
  const [open, setOpen] = useState(true);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const openDivisionMenu = Boolean(anchorEl);
  const navigate = useNavigate();


  // useEffect(() => {
  //   // Get division from localStorage
  //   const divisionData = localStorage.getItem("selectedDivision");
  //   if (divisionData) {
  //     try {
  //       setSelectedDivision(JSON.parse(divisionData));
  //     } catch (e) {
  //       console.error("Error parsing division data:", e);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const divisionData = localStorage.getItem("selectedDivision");

    if (divisionData) {
      try {
        setSelectedDivision(JSON.parse(divisionData));
      } catch (e) {
        console.error("Error parsing division data:", e);
      }
    }

    fetchDivisions();
  }, []);

  const fetchDivisions = async () => {
    try {
      const response = await DivisionList();
      setDivisions(response.data || response);
    } catch (err) {
      console.error("Error fetching divisions:", err);
    }
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleChangeDivision = (event) => {
    setAnchorEl(event.currentTarget);
  };


  const handleCloseDivisionMenu = () => {
    setAnchorEl(null);
  };

 const handleDivisionSelect = (division) => {
  setSelectedDivision(division);
  storeDivision(division);
  localStorage.setItem("selectedDivision", JSON.stringify(division));
  setAnchorEl(null);

  // Reload page after division change
  setTimeout(() => {
    window.location.reload();
  }, 300);
};

  return (
    <>
      <StyledDrawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          width: open ? "200px" : "64px",
          transition: "width 0.3s ease",
          zIndex: 1200,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            component="img"
            src="/logo13.png"
            alt="Logo"
            sx={{
              width: open ? 150 : 40,
              height: "auto",
              display: "block",
              mx: "auto",
              transition: "width 0.3s ease",
              my: 1.5,
            }}
          />
        </Box>

        <Divider />

        {/* Division Display Section */}
        {selectedDivision && (
          <Box
            sx={{
              mx: 1,
              my: 1.5,
              p: open ? 1.5 : 1,
              borderRadius: 2,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "space-between" : "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
            }}
          >
            {open ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {selectedDivision.division}
                  </Typography>
                </Box>
                <Tooltip title="Change Division" arrow>
                  <IconButton
                    size="small"
                    onClick={handleChangeDivision}
                    sx={{
                      color: "white",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                        transform: "rotate(180deg)",
                      },
                      transition: "all 0.3s ease",
                      width: 28,
                      height: 28,
                    }}
                  >
                    <SwapHorizIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip
                title={`${selectedDivision.division} - Click to change`}
                arrow
                placement="right"
              >
                <IconButton
                  size="small"
                  onClick={handleChangeDivision}
                  sx={{
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* No Division Selected State */}
        {!selectedDivision && (
          <>
            {open ? (
              <Box
                sx={{
                  m: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "warning.main",
                  backgroundColor: "warning.lighter",
                  textAlign: "center",
                }}
              >
                <BusinessIcon
                  sx={{
                    fontSize: 28,
                    color: "warning.main",
                    mb: 0.5,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "warning.dark",
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  No Division Selected
                </Typography>
                <Chip
                  label="Select Division"
                  size="small"
                  icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                  onClick={handleChangeDivision}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "warning.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "warning.dark",
                    },
                  }}
                />
              </Box>
            ) : (
              <Tooltip title="Select Division" arrow placement="right">
                <IconButton
                  onClick={handleChangeDivision}
                  sx={{
                    mx: "auto",
                    my: 1,
                    display: "flex",
                    width: 40,
                    height: 40,
                    backgroundColor: "warning.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "warning.dark",
                    },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Divider />
          </>
        )}
        <Menu
          anchorEl={anchorEl}
          open={openDivisionMenu}
          onClose={handleCloseDivisionMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              width: 230,
              maxHeight: 330,
              mt: 1,
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 12px 35px rgba(0,0,0,0.18)",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
              Change Division
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", opacity: 0.85 }}>
              Select your working division
            </Typography>
          </Box>

          <Box sx={{ maxHeight: 260, overflowY: "auto", py: 0.5 }}>
            {divisions.length > 0 ? (
              divisions.map((item) => {
                const isSelected =
                  selectedDivision?.division_id === item.division_id;

                return (
                  <MenuItem
                    key={item.division_id}
                    selected={isSelected}
                    onClick={() => handleDivisionSelect(item)}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 2,
                      fontSize: "0.85rem",
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "primary.main" : "text.primary",
                      backgroundColor: isSelected
                        ? "rgba(25, 118, 210, 0.08)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.12)",
                        transform: "translateX(3px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <BusinessIcon
                      sx={{
                        fontSize: 18,
                        mr: 1,
                        color: isSelected ? "primary.main" : "text.secondary",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      {item.division}
                    </Box>
                    {isSelected && (
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
                        Active
                      </Typography>
                    )}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem disabled>No divisions found</MenuItem>
            )}
          </Box>
        </Menu>

        {/* Scrollable Content */}
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 220px)",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <MenuContent />
        </Box>

        <Divider />

        {/* Fixed Footer Stack */}
        <Stack
          direction="row"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: open ? "200px" : "64px",
            p: 1,
            gap: open ? 1 : 0,
            alignItems: "center",
            zIndex: 1300,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            transition: "all 0.3s ease",
            justifyContent: open ? "flex-start" : "center",
          }}
        >
          <Avatar
            sizes="small"
            alt="User"
            sx={{
              width: 32,
              height: 32,
              marginLeft: open ? 0.5 : 0,
              transition: "all 0.3s ease",
              bgcolor: "primary.main",
              fontSize: "0.875rem",
            }}
          >
            {localStorage.getItem("name")?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>

          {open && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {localStorage.getItem("name")?.toUpperCase()}
              </Typography>
            </Box>
          )}
          <OptionsMenu />
        </Stack>
      </StyledDrawer>
    </>
  );
}
